/**
 * Which failures are worth asking again about, and how long to wait.
 *
 * **Retries are off everywhere else on purpose**, and this does not reopen
 * that. `main.tsx` sets `retry: false` on every query and mutation because the
 * API answers a refusal with a machine code a screen renders, and re-issuing a
 * refused request only delays showing the reason. That argument holds for a
 * refusal and says nothing about a connection that dropped — which is the
 * whole distinction this file exists to draw.
 *
 * The complaint it answers: reading a composed source during Add Source
 * "usually fails on the first and second attempt". That scan is one request
 * per address — thirteen for PFF's FCS template — and the first failure threw
 * the twelve that had already answered away, so a reviewer sat re-pressing a
 * button until a run of thirteen happened to come back clean. The odds are
 * worth saying out loud: at a 10% per-address failure rate, a thirteen-address
 * scan fails about **three times in four**, and three attempts per address
 * take that under 2%.
 *
 * **The backend already retries the publisher** — `Fetcher._fetch` gives a 429
 * or a 5xx three attempts with exponential backoff (`request_max_retries`), so
 * this is not that retry moved into the browser. It is the layer above: the
 * portal's own answer to *the browser*, where a dropped connection, a gateway
 * that gave up on a slow read, or a source still unreachable after the
 * Fetcher's own three tries all arrive as one failed call with twelve good
 * reads behind it.
 */

import type { ApiError } from "./api";

/**
 * The API's own failure shape, recognised structurally rather than by class.
 *
 * **A type-only import on purpose.** `npm test` is bare
 * `node --experimental-strip-types` over `tests/`, and `tsconfig` sets
 * `allowImportingTsExtensions: false`, so a *value* import of `./api` here is
 * one node cannot resolve and this policy would be untestable — which for a
 * rule about when to give up is the one thing it must not be.
 *
 * Tight enough to mean it: an `Error` (which `ApiError` extends) carrying both
 * a numeric status and a string code. A `TypeError` from a real fault has
 * neither.
 */
function apiFailure(error: unknown): ApiError | null {
  if (!(error instanceof Error)) return null;
  const candidate = error as Partial<ApiError>;
  const shaped = typeof candidate.status === "number" && typeof candidate.code === "string";
  return shaped ? (error as ApiError) : null;
}

/** How many times one address is asked before the scan gives up on it. */
export const MAX_ATTEMPTS = 3;

/**
 * Failures that are decisions rather than accidents, whatever their status.
 *
 * `RunFailure` reaches the browser as a 502 carrying the Fetcher's own code
 * (`discovery.py`), so status alone would have this retrying a rejected
 * credential three times and making the reviewer wait through two backoffs to
 * be told the same thing. Each of these needs a human to change something —
 * paste a fresh cookie, clear a multi-factor prompt, widen a budget — and asking
 * again unchanged cannot produce a different answer.
 */
const DELIBERATE = new Set([
  "credential_rejected",
  "multi_factor_required",
  "source_refused_client",
  "unsafe_next_link",
  "request_budget_exceeded",
  "source_shape_changed",
  "identity_collision",
  "no_players",
  "stale_source",
  "output_path_invalid",
  // Raised when the screen that started a read goes away. Asking again would
  // start the wait over only to abandon it on the next poll, three times, and
  // nobody is there to read the outcome either way. The read itself is
  // untouched on the server — see `discovery-job.ts`.
  "poll_abandoned",
]);

/**
 * Statuses that mean "ask again", short of the 5xx range.
 *
 * `0` is this client's own marker for a request that never reached the API at
 * all — see `request` in `api.ts`. It is the dropped-connection case and the
 * most retryable thing here.
 */
const TRANSIENT_STATUSES = new Set([0, 408, 425, 429]);

/**
 * Whether asking again could plausibly answer differently.
 *
 * Anything that is not an `ApiError` is **not** retried: every failure the API
 * can produce arrives as one, so a different exception is a fault in this
 * frontend, and looping on a bug wastes a reviewer's time three times over
 * instead of once.
 */
export function isRetryable(error: unknown): boolean {
  const failure = apiFailure(error);
  if (!failure) return false;
  if (DELIBERATE.has(failure.code)) return false;
  return TRANSIENT_STATUSES.has(failure.status) || failure.status >= 500;
}

/**
 * How long to wait before attempt `attempt + 1`, in milliseconds.
 *
 * Exponential from 500ms, which is `request_backoff_base_seconds` on the
 * server — the same ladder, so a reviewer watching the scan sees one rhythm
 * rather than two interleaved. Capped at four seconds: thirteen addresses
 * retrying twice each is already a long wait on a screen somebody is watching,
 * and a gateway that needs longer than four seconds is not coming back inside
 * this scan.
 */
export function retryDelayMs(attempt: number): number {
  return Math.min(500 * 2 ** (attempt - 1), 4000);
}

/** Paused between attempts; injectable so a test is not a real wait. */
export interface RetryOptions {
  /** Told which attempt is about to be made, for anything showing progress. */
  onRetry?: (attempt: number, delayMs: number, error: ApiError) => void;
  sleep?: (ms: number) => Promise<void>;
  maxAttempts?: number;
}

const realSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Run `work`, asking again while the failure looks like an accident.
 *
 * The **last** failure is thrown, not the first: it is the one the reviewer
 * needs to act on, and it is the one carrying how the source answered after
 * being given every chance this policy allows.
 */
export async function withRetry<T>(
  work: () => Promise<T>,
  { onRetry, sleep = realSleep, maxAttempts = MAX_ATTEMPTS }: RetryOptions = {},
): Promise<T> {
  let attempt = 0;
  for (;;) {
    attempt += 1;
    try {
      return await work();
    } catch (error) {
      if (attempt >= maxAttempts || !isRetryable(error)) throw error;
      const delayMs = retryDelayMs(attempt);
      onRetry?.(attempt + 1, delayMs, error as ApiError);
      await sleep(delayMs);
    }
  }
}
